import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  BarChart as StatsIcon
} from '@mui/icons-material';
import axios from 'axios';
import SurveyStats from '../../components/SurveyStats';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORY_LABELS = {
  demographics: 'Демография',
  family: 'Семья и быт',
  shopping: 'Покупки и финансы',
  travel: 'Путешествия и транспорт',
  auto: 'Авто и транспорт'
};

export default function ProfileSurveys() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedSurveyForStats, setSelectedSurveyForStats] = useState(null);
  const [statsResponses, setStatsResponses] = useState([]);

  useEffect(() => {
    loadProfileSurveys();
  }, []);

  const loadProfileSurveys = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys?profileSurveysOnly=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setSurveys(data.surveys || data || []);
      setError('');
    } catch (err) {
      console.error('Error loading profile surveys:', err);
      setError('Не удалось загрузить профильные опросы');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/admin/survey-builder?profileSurvey=true');
  };

  const handleEdit = (id) => {
    navigate(`/admin/survey-builder/${id}`);
  };

  const handleDeleteClick = (survey) => {
    setSurveyToDelete(survey);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!surveyToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/surveys/${surveyToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDeleteDialogOpen(false);
      setSurveyToDelete(null);
      loadProfileSurveys();
    } catch (err) {
      console.error('Error deleting survey:', err);
      setError(err.response?.data?.error || 'Не удалось удалить опрос');
      setDeleteDialogOpen(false);
    }
  };

  const openStats = async (survey) => {
    try {
      setSelectedSurveyForStats(survey);
      setStatsDialogOpen(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_BASE}/surveys/${survey.id}/responses`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const responsesData = Array.isArray(data) ? data : [];
      setStatsResponses(responsesData);
    } catch (e) {
      console.error('Failed to load stats', e);
      setStatsResponses([]);
    }
  };

  const closeStats = () => {
    setStatsDialogOpen(false);
    setSelectedSurveyForStats(null);
    setStatsResponses([]);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Профильные опросы
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Создать профильный опрос
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          Профильные опросы заполняются респондентами для формирования их профиля. 
          Ответы автоматически обновляют соответствующие поля профиля респондента.
        </Alert>

        {surveys.length === 0 ? (
          <Alert severity="warning">
            Профильных опросов пока нет. Создайте первый опрос нажав кнопку "Создать профильный опрос".
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Вопросов</TableCell>
                  <TableCell>Ответов</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {surveys.map((survey) => (
                  <TableRow key={survey.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {survey.title}
                      </Typography>
                      {survey.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {survey.description.substring(0, 100)}
                          {survey.description.length > 100 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          survey.status === 'active' ? 'Активен' :
                          survey.status === 'draft' ? 'Черновик' :
                          survey.status === 'pending_moderation' ? 'На модерации' :
                          survey.status === 'rejected' ? 'Отклонён' :
                          survey.status
                        }
                        size="small"
                        color={
                          survey.status === 'active' ? 'success' :
                          survey.status === 'draft' ? 'default' :
                          survey.status === 'pending_moderation' ? 'warning' :
                          'error'
                        }
                      />
                    </TableCell>
                    <TableCell>{survey.questions?.length || 0}</TableCell>
                    <TableCell>{survey.responseCount ?? 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => openStats(survey)}
                        title="Статистика"
                      >
                        <StatsIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(survey.id)}
                        title="Редактировать"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(survey)}
                        title="Удалить"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Удалить профильный опрос?</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить профильный опрос "{surveyToDelete?.title}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Это действие нельзя отменить. Все связанные данные будут потеряны.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statistics Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={closeStats}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: '2px solid #e0e0e0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 700
        }}>
          Статистика профильного опроса: {selectedSurveyForStats?.title || ''}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {selectedSurveyForStats && (
            <SurveyStats 
              survey={selectedSurveyForStats} 
              responses={statsResponses}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button 
            onClick={closeStats}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
              }
            }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
